import { MeetingFormData, ResolvedMatch, Employee, TimeSlot, AttendeePriority } from '../interfaces/interface';
import { formatTimeSlotDisplay, getUserTimezone } from 'src/utilities/DateTimeUtilities';
import { logger } from 'src/logger';
import { getOutlookDeeplinkUrls } from '../constants/EAHuddleConstants';
import { LeaderEAConfigService } from './LeaderEAConfigService';
import { DateTime } from 'luxon';

/**
 * Service for handling meeting-related business logic and operations
 */
export class MeetingService {
  private static readonly OUTLOOK_URL_LIMIT = 25000;

  /**
   * Checks if an email address is a custom EA email from the leader EA config.
   * Custom emails should be formatted without names for Outlook compatibility.
   */
  private static isCustomEAEmail(email: string): boolean {
    const configs = LeaderEAConfigService.getAllCached();
    return configs.some((config) => config.eaEmailAddress === email);
  }

  /**
   * Formats an EA email for Outlook. Custom emails are formatted without names.
   */
  private static formatEAEmail(ea: { name: string; email: string }, useQuotes = false): string {
    if (this.isCustomEAEmail(ea.email)) {
      return ea.email;
    }
    return useQuotes ? `"${ea.name}" <${ea.email}>` : `${ea.name} <${ea.email}>`;
  }

  static canCreateMeeting(resolvedAttendees: ResolvedMatch[]): {
    canCreate: boolean;
    issues: string[];
  } {
    const issues: string[] = [];

    const requiredAttendees = resolvedAttendees.filter((a) => a.priority === 'critical' || a.priority === 'required');

    if (requiredAttendees.length === 0) {
      issues.push('At least one required attendee is needed');
    }

    const unresolvedRequired = requiredAttendees.filter((a) => !a.resolvedEmail);
    if (unresolvedRequired.length > 0) {
      issues.push(`${unresolvedRequired.length} required attendees need email resolution`);
    }

    return {
      canCreate: issues.length === 0,
      issues
    };
  }

  static buildAttendeeData(resolvedAttendees: ResolvedMatch[]) {
    const requiredPeople = resolvedAttendees
      .filter((a) => (a.priority === 'critical' || a.priority === 'required') && a.resolvedEmail)
      .map((a) => ({
        name: a.editedName || a.selectedEmployee?.displayName || a.selectedEmployee?.employee_name || a.inputName,
        email: a.resolvedEmail!
      }));

    const optionalPeople = resolvedAttendees
      .filter((a) => a.priority === 'optional' && a.resolvedEmail)
      .map((a) => ({
        name: a.editedName || a.selectedEmployee?.displayName || a.selectedEmployee?.employee_name || a.inputName,
        email: a.resolvedEmail!
      }));

    // Deduplicate by email - prioritize required over optional
    const allEmails = new Set<string>();
    const finalRequiredWithNames: Array<{ name: string; email: string }> = [];
    const finalOptionalWithNames: Array<{ name: string; email: string }> = [];

    for (const person of requiredPeople) {
      if (!allEmails.has(person.email.toLowerCase())) {
        allEmails.add(person.email.toLowerCase());
        finalRequiredWithNames.push(person);
      }
    }

    for (const person of optionalPeople) {
      if (!allEmails.has(person.email.toLowerCase())) {
        allEmails.add(person.email.toLowerCase());
        finalOptionalWithNames.push(person);
      }
    }

    return {
      requiredEmails: finalRequiredWithNames.map((p) => p.email),
      optionalEmails: finalOptionalWithNames.map((p) => p.email),
      requiredWithNames: finalRequiredWithNames,
      optionalWithNames: finalOptionalWithNames
    };
  }

  private static buildOutlookDeeplinkUrl(baseUrl: string, params: URLSearchParams, bodyContent: string | undefined, urlType: string): string {
    // Build full URL
    let finalUrl = `${baseUrl}?${params.toString()}`;

    // If URL exceeds Outlook's limit and there's body content, replace with placeholder
    if (finalUrl.length > MeetingService.OUTLOOK_URL_LIMIT && bodyContent) {
      const originalLength = finalUrl.length;
      const placeholderMessage = 'IMPORTANT: Please copy and paste the meeting description here as it was too long to include automatically.';
      params.set('body', placeholderMessage);
      finalUrl = `${baseUrl}?${params.toString()}`;

      logger.warn(
        `${urlType} URL exceeded ${MeetingService.OUTLOOK_URL_LIMIT} characters ` +
          `(Limit: ${MeetingService.OUTLOOK_URL_LIMIT}, Actual: ${originalLength}). ` +
          `Description replaced with placeholder.`
      );
    }

    return finalUrl;
  }

  static async generateOutlookWebUrl(
    formData: MeetingFormData,
    resolvedAttendees: ResolvedMatch[],
    selectedSlot?: TimeSlot,
    senderName?: string,
    senderAlias?: string,
    onBehalfOfName?: string | null,
    coOrganizerName?: string | null
  ): Promise<string> {
    const attendeeData = this.buildAttendeeData(resolvedAttendees);
    const bodyContent = this.generateMeetingBodyHtml(
      formData,
      resolvedAttendees,
      selectedSlot,
      senderName,
      senderAlias,
      onBehalfOfName,
      coOrganizerName
    );

    const { calendarUrl } = await getOutlookDeeplinkUrls();
    const baseUrl = calendarUrl;
    const params = new URLSearchParams();

    if (formData.subject) {
      params.append('subject', formData.subject);
    }

    if (selectedSlot) {
      const effectiveTz = selectedSlot.timezone || getUserTimezone();
      const startDt = DateTime.fromFormat(`${selectedSlot.date} ${selectedSlot.startTime}`, 'yyyy-MM-dd HH:mm', { zone: effectiveTz });
      let endDt = DateTime.fromFormat(`${selectedSlot.date} ${selectedSlot.endTime}`, 'yyyy-MM-dd HH:mm', { zone: effectiveTz });
      if (selectedSlot.endTime === '00:00') {
        endDt = endDt.plus({ days: 1 });
      }
      params.append('startdt', startDt.toFormat("yyyy-MM-dd'T'HH:mm:ssZZ"));
      params.append('enddt', endDt.toFormat("yyyy-MM-dd'T'HH:mm:ssZZ"));
    }

    if (attendeeData.requiredWithNames && attendeeData.requiredWithNames.length > 0) {
      const formattedRequired = attendeeData.requiredWithNames.map((person) => `"${person.name}" <${person.email}>`);
      params.append('to', formattedRequired.join(','));
    } else if (attendeeData.requiredEmails.length > 0) {
      params.append('to', attendeeData.requiredEmails.join(','));
    }

    if (attendeeData.optionalWithNames && attendeeData.optionalWithNames.length > 0) {
      const formattedOptional = attendeeData.optionalWithNames.map((person) => `"${person.name}" <${person.email}>`);
      params.append('cc', formattedOptional.join(','));
    } else if (attendeeData.optionalEmails.length > 0) {
      params.append('cc', attendeeData.optionalEmails.join(','));
    }

    if (bodyContent) {
      params.append('body', bodyContent);
    }

    return this.buildOutlookDeeplinkUrl(baseUrl, params, bodyContent, 'Outlook Calendar');
  }

  /**
   * Generates an HTML table body for the Outlook meeting invite, matching the EA meeting request template format.
   * Includes attendees grouped by priority with proxy details in brackets.
   */
  static generateMeetingBodyHtml(
    formData: MeetingFormData,
    resolvedAttendees: ResolvedMatch[],
    selectedSlot?: TimeSlot,
    senderName?: string,
    senderAlias?: string,
    onBehalfOfName?: string | null,
    coOrganizerName?: string | null
  ): string {
    const esc = (s: string): string =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    const cellStyle = 'border:1px solid #000;padding:8px;vertical-align:top;';
    const headerStyle = `${cellStyle}font-weight:bold;width:30%;`;

    const formatAttendee = (a: ResolvedMatch): string => {
      const emp = a.selectedEmployee;
      const name = esc(a.editedName || emp?.employee_name || a.inputName);
      const level = emp?.job_level_name ? ` (L${esc(emp.job_level_name)})` : '';
      const title = emp?.job_title_name ? `, ${esc(emp.job_title_name)}` : '';
      const proxy = a.proxyForLogin ? ` [Proxy for ${esc(a.proxyForName || a.proxyForLogin)}]` : '';
      const ea = a.selectedExecutiveAssistant?.employee_name ? ` (EA: ${esc(a.selectedExecutiveAssistant.employee_name)})` : '';
      return `${name}${level}${title}${proxy}${ea}`;
    };

    const formatAttendeeList = (attendees: ResolvedMatch[]): string =>
      attendees.length > 0 ? `<ol>${attendees.map((a) => `<li>${formatAttendee(a)}</li>`).join('')}</ol>` : 'N/A';

    const critical = resolvedAttendees.filter((a) => a.priority === 'critical');
    const required = resolvedAttendees.filter((a) => a.priority === 'required');
    const optional = resolvedAttendees.filter((a) => a.priority === 'optional');

    const agendaHtml = esc((formData.agenda || 'N/A').trim()).replace(/\n/g, '<br/>');

    const contactInfo = senderName && senderAlias ? `${esc(senderName)}, ${esc(senderAlias)}@` : 'the meeting organizer';
    const rows = [
      `<tr><td colspan="2" style="${cellStyle}text-align:center;"><strong>PLEASE DO NOT FORWARD THIS INVITE</strong><br/><em>Forwarding meetings breaks confidentiality &amp; frequently corrupts invites</em><br/><em>Please reach out to ${contactInfo} for any scheduling questions.</em></td></tr>`,
      `<tr><td style="${headerStyle}">Meeting Title:</td><td style="${cellStyle}">${esc(formData.subject || 'Untitled Meeting')}</td></tr>`,
      `<tr><td style="${headerStyle}">Agenda:</td><td style="${cellStyle}">${agendaHtml}</td></tr>`
    ];

    if (formData.onBehalfOfLogin) {
      const displayName = onBehalfOfName || formData.onBehalfOfLogin;
      rows.push(`<tr><td style="${headerStyle}">Topic Owner:</td><td style="${cellStyle}">${esc(displayName)}</td></tr>`);
    }

    if (formData.coOrganizerLogin) {
      const coOrgDisplay = coOrganizerName || formData.coOrganizerLogin;
      rows.push(`<tr><td style="${headerStyle}">Co-organizer:</td><td style="${cellStyle}">${esc(coOrgDisplay)}</td></tr>`);
    }

    rows.push(`<tr><td style="${headerStyle}">Doc Owner:</td><td style="${cellStyle}">&nbsp;</td></tr>`);

    if (critical.length > 0) {
      rows.push(`<tr><td style="${headerStyle}">Critical Attendees:</td><td style="${cellStyle}">${formatAttendeeList(critical)}</td></tr>`);
    }
    rows.push(`<tr><td style="${headerStyle}">Required Attendees:</td><td style="${cellStyle}">${formatAttendeeList(required)}</td></tr>`);
    if (optional.length > 0) {
      rows.push(`<tr><td style="${headerStyle}">Optional Attendees:</td><td style="${cellStyle}">${formatAttendeeList(optional)}</td></tr>`);
    }

    return `<table style="border-collapse:collapse;border:1px solid #000;width:50%;">${rows.join('')}</table>`;
  }

  static collectExecutiveAssistants(resolvedAttendees: ResolvedMatch[]): Array<{ name: string; email: string }> {
    const allEAs: Array<{ name: string; email: string }> = [];
    const seenEmails = new Set<string>();

    resolvedAttendees.forEach((attendee) => {
      if (attendee.selectedExecutiveAssistant) {
        const email = attendee.selectedExecutiveAssistant.employee_internal_email_address?.toLowerCase();
        if (email && !seenEmails.has(email)) {
          seenEmails.add(email);
          allEAs.push({
            name: attendee.selectedExecutiveAssistant.employee_name,
            email: attendee.selectedExecutiveAssistant.employee_internal_email_address
          });
        }
      }
    });

    return allEAs;
  }

  static generateEASubject(formData: MeetingFormData): string {
    return `Action Needed: Vote on Meeting Time for ${formData.subject}`;
  }

  /**
   * Generate EA email body matching the voting template format
   */
  static generateEABody(
    formData: MeetingFormData,
    resolvedAttendees: ResolvedMatch[],
    timeSlots?: TimeSlot[],
    meetingId?: string | null,
    creatorAlias?: string | null
  ): string {
    const subject = formData.subject;
    let body = `Hi Team,

Looking to lock in a time for ${subject || '[meeting name]'}. I've proposed a couple of time slots, please vote on what works best for you:`;

    if (meetingId && creatorAlias) {
      const voteUrl = `${window.location.origin}/ea-huddle?meetingId=${meetingId}&creator=${encodeURIComponent(creatorAlias)}`;
      body += `\n\n👉 Vote here: ${voteUrl}`;
    }

    if (formData.agenda?.trim()) {
      body += `\n\n📋 Agenda:\n\n${formData.agenda.trim()}\n`;
    }

    if (timeSlots && timeSlots.length > 0) {
      body += '\n\nProposed Meeting Windows:\n';
      timeSlots.forEach((slot) => {
        body += `\n• ${formatTimeSlotDisplay(slot.date, slot.startTime, slot.endTime, slot.timezone)}`;
      });
    }

    // Attendees grouped by priority
    const critical = resolvedAttendees.filter((a) => a.priority === 'critical');
    const required = resolvedAttendees.filter((a) => a.priority === 'required');
    const optional = resolvedAttendees.filter((a) => a.priority === 'optional');

    const formatAttendee = (a: ResolvedMatch) => {
      const emp = a.selectedEmployee;
      const name = a.editedName || emp?.employee_name || a.inputName;
      const level = emp?.job_level_name ? ` (L${emp.job_level_name})` : '';
      const title = emp?.job_title_name ? `, ${emp.job_title_name}` : '';
      const proxy = a.proxyForLogin ? ` [Proxy for ${a.proxyForName || a.proxyForLogin}]` : '';
      return `${name}${level}${title}${proxy}`;
    };

    if (critical.length > 0 || required.length > 0 || optional.length > 0) {
      body += '\n\nAttendees:';

      if (critical.length > 0) {
        body += '\n\n⭐ Critical';
        critical.forEach((a) => {
          body += `\n• ${formatAttendee(a)}`;
        });
      }
      if (required.length > 0) {
        body += '\n\nRequired';
        required.forEach((a) => {
          body += `\n• ${formatAttendee(a)}`;
        });
      }
      if (optional.length > 0) {
        body += '\n\nOptional';
        optional.forEach((a) => {
          body += `\n• ${formatAttendee(a)}`;
        });
      }
    }

    // EA Coordination section
    const eaMappings: string[] = [];
    resolvedAttendees.forEach((a) => {
      const ea = a.selectedExecutiveAssistant;
      if (ea?.employee_name && ea?.employee_internal_email_address) {
        const attendeeName = a.editedName || a.selectedEmployee?.employee_name || a.inputName;
        eaMappings.push(`${ea.employee_name}, EA for ${attendeeName}`);
      }
    });

    if (eaMappings.length > 0) {
      body += '\n\nEA Coordination';
      eaMappings.forEach((m) => {
        body += `\n• ${m}`;
      });
    }
    return body;
  }

  static formatAttendeesForCopy(resolvedAttendees: ResolvedMatch[]): string {
    const attendeeData = this.buildAttendeeData(resolvedAttendees);
    const allAttendees = [...attendeeData.requiredWithNames, ...attendeeData.optionalWithNames];

    return allAttendees.map((person) => `${person.name} <${person.email}>`).join('; ');
  }

  private static getUniqueAttendeesByPriority(
    resolvedAttendees: ResolvedMatch[],
    priorities?: ReadonlyArray<AttendeePriority>
  ): Array<{ name: string; email: string }> {
    const seenEmails = new Set<string>();
    const filtered: Array<{ name: string; email: string }> = [];
    resolvedAttendees.forEach((a) => {
      if (a.resolvedEmail && (!priorities || priorities.includes(a.priority))) {
        const emailLower = a.resolvedEmail.toLowerCase();
        if (!seenEmails.has(emailLower)) {
          seenEmails.add(emailLower);
          const name = a.editedName || a.selectedEmployee?.displayName || a.selectedEmployee?.employee_name || a.inputName;
          filtered.push({ name, email: a.resolvedEmail });
        }
      }
    });
    return filtered;
  }

  static formatAttendeesForCopyByPriorities(
    resolvedAttendees: ResolvedMatch[],
    priorities: ReadonlyArray<AttendeePriority>
  ): { formatted: string; count: number } {
    const result = this.getUniqueAttendeesByPriority(resolvedAttendees, priorities);
    return {
      formatted: result.map((person) => `${person.name} <${person.email}>`).join('; '),
      count: result.length
    };
  }

  static countAttendeesWithEmailByPriorities(resolvedAttendees: ResolvedMatch[], priorities: ReadonlyArray<AttendeePriority>): number {
    return this.getUniqueAttendeesByPriority(resolvedAttendees, priorities).length;
  }

  static formatExecutiveAssistantsForCopy(resolvedAttendees: ResolvedMatch[]): string {
    const executiveAssistants = this.collectExecutiveAssistants(resolvedAttendees);

    return executiveAssistants.map((ea) => this.formatEAEmail(ea)).join('; ');
  }

  private static formatEmployeeDetails(employee: Employee): string {
    // Combine name and login into "Name (login@)" format
    const login = employee.login_name?.toLowerCase() || '';
    const nameWithLogin = employee.employee_name ? `${employee.employee_name} (${login}@)` : login ? `${login}@` : '';

    return [
      nameWithLogin,
      employee.job_title_name || '',
      employee.job_level_name || '',
      employee.location_city_name || '',
      employee.department_name_current || ''
    ]
      .filter(Boolean)
      .join(', ');
  }

  static formatEmployeeDetailsForCopy(resolvedAttendees: ResolvedMatch[]): string {
    const employeeDetails: string[] = [];

    resolvedAttendees.forEach((attendee) => {
      if (attendee.selectedEmployee) {
        employeeDetails.push(this.formatEmployeeDetails(attendee.selectedEmployee));
      }
    });

    return employeeDetails.join('\n');
  }

  static formatExecutiveAssistantDetailsForCopy(resolvedAttendees: ResolvedMatch[]): string {
    const eaDetails: string[] = [];
    const seenLogins = new Set<string>();

    resolvedAttendees.forEach((attendee) => {
      if (attendee.selectedExecutiveAssistant) {
        const ea = attendee.selectedExecutiveAssistant;
        const login = ea.login_name?.toLowerCase() || '';

        // Only add if not already seen (with fallback for missing logins)
        if (!seenLogins.has(login)) {
          seenLogins.add(login);
          eaDetails.push(this.formatEmployeeDetails(ea));
        }
      }
    });

    return eaDetails.join('\n');
  }

  /**
   * Collect EAs grouped by their attendee's priority, plus attendees without EAs.
   */
  static collectEAsWithPriority(resolvedAttendees: ResolvedMatch[]): {
    criticalEAs: Array<{ name: string; email: string }>;
    otherEAs: Array<{ name: string; email: string }>;
    attendeesWithoutEA: Array<{ name: string; email: string }>;
  } {
    const criticalEAs: Array<{ name: string; email: string }> = [];
    const otherEAs: Array<{ name: string; email: string }> = [];
    const attendeesWithoutEA: Array<{ name: string; email: string }> = [];
    const seenEAEmails = new Set<string>();

    resolvedAttendees.forEach((attendee) => {
      const ea = attendee.selectedExecutiveAssistant;
      const attendeeEmail = attendee.resolvedEmail?.toLowerCase();

      if (ea && ea.employee_internal_email_address?.trim()) {
        const eaEmail = ea.employee_internal_email_address.toLowerCase();
        if (!seenEAEmails.has(eaEmail)) {
          seenEAEmails.add(eaEmail);
          const eaEntry = { name: ea.employee_name, email: ea.employee_internal_email_address };

          if (attendee.priority === 'critical') {
            criticalEAs.push(eaEntry);
          } else {
            otherEAs.push(eaEntry);
          }
        }
      } else if (attendeeEmail) {
        const attendeeName = attendee.editedName || attendee.selectedEmployee?.employee_name || attendee.inputName;
        attendeesWithoutEA.push({ name: attendeeName, email: attendee.resolvedEmail! });
      }
    });

    return { criticalEAs, otherEAs, attendeesWithoutEA };
  }

  static async generateEAMailtoUrl(
    formData: MeetingFormData,
    resolvedAttendees: ResolvedMatch[],
    timeSlots?: TimeSlot[],
    meetingId?: string | null,
    creatorAlias?: string | null,
    deadline?: string,
    senderName?: string,
    senderAlias?: string
  ): Promise<string> {
    const { criticalEAs, otherEAs, attendeesWithoutEA } = this.collectEAsWithPriority(resolvedAttendees);
    const allEAs = [...criticalEAs, ...otherEAs];

    if (allEAs.length === 0 && attendeesWithoutEA.length === 0) {
      throw new Error('No executive assistants or attendees found');
    }

    const eaSubject = this.generateEASubject(formData);
    const bodyContent = this.generateEABody(formData, resolvedAttendees, timeSlots, meetingId, creatorAlias);

    const allToRecipients = [
      ...criticalEAs.map((ea) => this.formatEAEmail(ea, true)),
      ...otherEAs.map((ea) => this.formatEAEmail(ea, true)),
      ...attendeesWithoutEA.map((person) => `"${person.name}" <${person.email}>`)
    ];

    return this.buildMailDeeplinkUrl(eaSubject, bodyContent, allToRecipients, 'EA Email');
  }

  /**
   * Build an Outlook mail deep link URL using encodeURIComponent (spaces as %20).
   * Truncates body with a placeholder if the URL exceeds the Outlook limit.
   */
  private static async buildMailDeeplinkUrl(subject: string, body: string, toRecipients: string[], urlType: string): Promise<string> {
    const { mailUrl } = await getOutlookDeeplinkUrls();
    const baseUrl = mailUrl;
    const encodedSubject = encodeURIComponent(subject);
    const encodedTo = toRecipients.length > 0 ? encodeURIComponent(toRecipients.join(',')) : '';

    const buildUrl = (encodedBody: string) => {
      const parts = [`subject=${encodedSubject}`];
      if (encodedTo) parts.push(`to=${encodedTo}`);
      parts.push(`body=${encodedBody}`);
      return `${baseUrl}?${parts.join('&')}`;
    };

    let finalUrl = buildUrl(encodeURIComponent(body));

    if (finalUrl.length > this.OUTLOOK_URL_LIMIT) {
      const placeholder = 'IMPORTANT: Please copy and paste the content here as it was too long to include automatically.';
      finalUrl = buildUrl(encodeURIComponent(placeholder));
      logger.warn(`${urlType} URL exceeded ${this.OUTLOOK_URL_LIMIT} characters. Body replaced with placeholder.`);
    }

    return finalUrl;
  }
  /**
   * Generate an Outlook mail deep link for a voting reminder email with the AI summary embedded.
   */
  static async generateReminderMailtoUrl(
    subject: string,
    summaryText: string,
    resolvedAttendees: ResolvedMatch[],
    meetingId?: string | null,
    creatorAlias?: string | null
  ): Promise<string> {
    const { criticalEAs, otherEAs, attendeesWithoutEA } = this.collectEAsWithPriority(resolvedAttendees);

    const allToRecipients = [
      ...criticalEAs.map((ea) => this.formatEAEmail(ea, true)),
      ...otherEAs.map((ea) => this.formatEAEmail(ea, true)),
      ...attendeesWithoutEA.map((person) => `"${person.name}" <${person.email}>`)
    ];

    const reminderSubject = `[Reminder] Action Needed: Vote on Meeting Time for ${subject || '[meeting name]'}`;
    const plainSummary = this.stripMarkdown(summaryText);

    const body =
      `Hi Team,\n\n` +
      `Friendly reminder — we're still waiting on votes to confirm the time slot for the ${subject || '[meeting name]'} meeting. ` +
      `If you've already voted, please disregard this message.\n\n` +
      (meetingId && creatorAlias
        ? `👉 Vote here: ${window.location.origin}/ea-huddle?meetingId=${meetingId}&creator=${encodeURIComponent(creatorAlias)}\n\n`
        : '') +
      `${plainSummary}\n\n` +
      `Please take a moment to vote at your earliest convenience so we can lock this in.`;

    return this.buildMailDeeplinkUrl(reminderSubject, body, allToRecipients, 'Reminder Email');
  }

  /**
   * Strip markdown formatting to plain text for Outlook deep link URLs.
   */
  private static stripMarkdown(text: string): string {
    return text
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/^---+$/gm, '')
      .replace(/^- /gm, '• ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  static findDuplicateEmails(resolvedAttendees: ResolvedMatch[]): Set<string> {
    const emailCounts = new Map<string, number>();
    const duplicates = new Set<string>();

    resolvedAttendees.forEach((attendee) => {
      const email = attendee.resolvedEmail?.toLowerCase();
      if (email) {
        const count = emailCounts.get(email) || 0;
        emailCounts.set(email, count + 1);
        if (count >= 1) {
          duplicates.add(email);
        }
      }
    });

    return duplicates;
  }

  static getMeetingStatistics(resolvedAttendees: ResolvedMatch[]) {
    const requiredAttendees = resolvedAttendees.filter((a) => a.priority === 'critical' || a.priority === 'required');
    const optionalAttendees = resolvedAttendees.filter((a) => a.priority === 'optional');
    const resolvedRequired = requiredAttendees.filter((a) => a.resolvedEmail);
    const resolvedOptional = optionalAttendees.filter((a) => a.resolvedEmail);
    const executiveAssistants = this.collectExecutiveAssistants(resolvedAttendees);
    const duplicateEmails = this.findDuplicateEmails(resolvedAttendees);

    return {
      totalAttendees: resolvedAttendees.length,
      requiredCount: requiredAttendees.length,
      optionalCount: optionalAttendees.length,
      resolvedRequiredCount: resolvedRequired.length,
      resolvedOptionalCount: resolvedOptional.length,
      unresolvedRequiredCount: requiredAttendees.length - resolvedRequired.length,
      unresolvedOptionalCount: optionalAttendees.length - resolvedOptional.length,
      executiveAssistantsCount: executiveAssistants.length,
      duplicateEmailsCount: duplicateEmails.size,
      canCreateMeeting: this.canCreateMeeting(resolvedAttendees).canCreate
    };
  }
}
